import { Injectable, NgZone } from '@angular/core';
import { State, Action, Selector, StateContext } from '@ngxs/store';
import { catchError, map } from 'rxjs/operators';
import { Observable, of } from 'rxjs';
import { PublicTrackingInitialState, PublicTrackingItemLoad } from './public-tracking-item.actions';
import { PublicTrackingItemService } from './public-tracking-item.service';
import { Error, CustomHttpErrorResponse, NotificationService } from '../../../../common';
import {
  ItemTrackingContentType,
  ItemTrackingContentTypeFromSAP,
  PublicTrackingHandlingUnit,
  PublicTrackingItemModel,
} from '../models/public-tracking.model';
import { Router } from '@angular/router';
import { MatSnackBarRef, SimpleSnackBar } from '@angular/material/snack-bar';

export interface PublicTrackingStateModel {
  loaded: boolean;
  overview: PublicTrackingItemModel;
}

const initialState: PublicTrackingStateModel = {
  loaded: false,
  overview: null,
};

@State<PublicTrackingStateModel>({
  name: 'publicTracking',
  defaults: initialState,
})
@Injectable()
export class PublicTrackingItemState {
  constructor(
    private publicTrackingSearchItemService: PublicTrackingItemService,
    private notificationService: NotificationService,
    private router: Router,
    private ngZone: NgZone,
  ) {}

  @Selector()
  static getState(state: PublicTrackingStateModel): PublicTrackingStateModel {
    return state;
  }

  @Selector()
  static getLoaded(state: PublicTrackingStateModel): boolean {
    return state.loaded;
  }

  @Selector()
  static getItem(state: PublicTrackingStateModel): PublicTrackingItemModel {
    return state.overview;
  }

  @Selector()
  static getHandlingUnits(state: PublicTrackingStateModel): PublicTrackingHandlingUnit[] {
    return state.overview.deliveryNotePublicView.handlingUnits;
  }

  @Action(PublicTrackingItemLoad)
  getItem(
    ctx: StateContext<PublicTrackingStateModel>,
    { payload }: PublicTrackingItemLoad,
  ): Observable<MatSnackBarRef<SimpleSnackBar> | void> {
    const { itemId, zipCode, trackingContentType, sapId } = payload;

    ctx.patchState({ loaded: false });

    return this.publicTrackingSearchItemService.getItem({ itemId, zipCode, trackingContentType, sapId }).pipe(
      map((overview: PublicTrackingItemModel) => {
        // TODO: check why we need it here
        this.ngZone.run(() => {
          ctx.patchState({ overview, loaded: true });

          const urlByItemId = `/public-tracking?${
            overview.trackingContentType === ItemTrackingContentType.DN
              ? ItemTrackingContentTypeFromSAP.DN
              : ItemTrackingContentTypeFromSAP.HU
          }=${payload.itemId}`;

          const urlByItemAndSapId = `${urlByItemId}&sapId=${sapId}`;

          this.router.navigateByUrl(sapId ? urlByItemAndSapId : urlByItemId);
        });
      }),
      catchError(({ error }: CustomHttpErrorResponse<Error>) => of(this.notificationService.error(error.message))),
    );
  }

  @Action(PublicTrackingInitialState)
  setInitialState(ctx: StateContext<PublicTrackingStateModel>): void {
    ctx.setState(initialState);
  }
}
